export type CardSource =
  | 'INBOUND_EMAIL'
  | 'INBOUND_WHATSAPP'   // a WhatsApp message from a tenant/owner/provider (#282)
  | 'INTERNAL_EVENT'
  | 'SCHEDULED_SWEEP';

/**
 * Sources that carry a message a HUMAN wrote to us, as opposed to something
 * the system raised. They share the whole "read the message, reply to it"
 * treatment in the UI — only the transport differs.
 */
export function isHumanMessageSource(source: CardSource | undefined): boolean {
  return source === 'INBOUND_EMAIL' || source === 'INBOUND_WHATSAPP';
}

export type CardIntent =
  | 'MAINTENANCE'
  | 'PAYMENT'
  | 'LEGAL'
  | 'INFO'
  | 'REMINDER'
  | 'RENEWAL'
  | 'INDEXATION'
  /**
   * Regulated obligation with a legal expiry: EPC/PEB, electrical inspection,
   * smoke detectors, boiler, the owner's insurance policy, a syndic mandate.
   * Distinct from REMINDER because the cost of missing one is not a late
   * payment — it can forbid letting the property or void a cover (issue #6).
   */
  | 'COMPLIANCE'
  /**
   * Something was asked and needs an answer (#367). Raised ALONGSIDE the action
   * card when one message carries both — the owner executes one and approves a
   * reply for the other, so they cannot share a card.
   */
  | 'QUESTION'
  | 'SPAM';

export type CardPriority = 'low' | 'medium' | 'high' | 'emergency';

export type CardActionType =
  | 'DRAFT_REPLY'
  | 'ADD_EXPENSE'
  /**
   * Opens a CASE (dossier) — the container for anything that is followed over
   * time without being a repair: a formal notice, a rental application, an
   * état des lieux, a sensitive-communication trail.
   *
   * NOT deprecated, whatever the previous tag on this line said. Audit #83
   * point 59 read that tag and proposed removing `createCaseExecutor` from the
   * registry; doing so would have left **12 live call sites across 9 scenarios**
   * — `legal.formal_notice`, `legal.bailiff_notice`, `lease.application`,
   * `lease.etat_des_lieux`, `_shared/sensitive-communication-scenario`
   * and four `maintenance.*` — proposing an effect with no executor, failing at
   * approve time on the one click the owner made on purpose.
   *
   * What the tag was really about is narrower and still open: the four
   * `maintenance.*` scenarios should raise `CREATE_REPAIR`, not a case. That is
   * a behaviour change (a repair and a case are different entities with
   * different downstream UI), so it belongs in its own change, not in a
   * deprecation comment.
   */
  | 'CREATE_CASE'
  | 'CREATE_REPAIR'
  | 'FIND_SERVICE_PROVIDER'
  /** Auto-send a job-request invitation to a known provider on a recurring maintenance. */
  | 'INVITE_PROVIDER'
  | 'WAIT_INBOUND'
  | 'UPDATE_CONTRACT'
  /** Requalifies a Belgian short-term lease into a 9-year main-residence lease. Mirrors backend `CONVERT_LEASE_TO_NINE_YEAR`. */
  | 'CONVERT_LEASE_TO_NINE_YEAR'
  | 'SEND_REMINDER'
  | 'APPLY_INDEXATION'
  | 'APPROVE_MAIL_DRAFT'
  /** Generic task creator — mirrors backend `CREATE_TASK`. See backend createTask.ts for the payload shape. */
  | 'CREATE_TASK'
  /** Seeds a structured move-out/move-in inventory report (draft). Mirrors backend `CREATE_INVENTORY_REPORT`. */
  | 'CREATE_INVENTORY_REPORT'
  /** Seeds a meter-transfer record (pending). Mirrors backend `CREATE_METER_TRANSFER`. */
  | 'CREATE_METER_TRANSFER'
  /** Marks the workflow's repair completed on the close step. Mirrors backend `COMPLETE_REPAIR`. */
  | 'COMPLETE_REPAIR'
  /** The owner's verdict on a rent the tenant declared paid. Mirrors backend `CONFIRM_TENANT_PAYMENT`. */
  | 'CONFIRM_TENANT_PAYMENT'
  /** Files an e-mail attachment (AG minutes) in Documents + the building's PV d'AG. Mirrors backend `FILE_DOCUMENT`. */
  | 'FILE_DOCUMENT'
  | 'NO_ACTION';

/**
 * Frontend mirror of a `workspaces/{wid}/cockpitTasks/{taskId}` Firestore
 * document, as written by the backend `CREATE_TASK` executor. Dates come
 * out of `collectionData()` as Firestore Timestamps with a `.toDate()`
 * method; older docs that landed as ISO strings stay supported via the
 * lenient `unknown` type and a defensive helper at the render site.
 */
export interface CockpitTask {
  id: string;
  workspaceId: string;
  category: string;
  title: string;
  description: string | null;
  status: 'open' | 'done' | 'cancelled';
  /** Firestore Timestamp, ISO string, or null. Normalize at render time. */
  dueDate?: unknown;
  propertyId: string | null;
  propertyName: string | null;
  contractId: string | null;
  contactId: string | null;
  extra?: Record<string, unknown> | null;
  /** Always `"cockpit"` — the only origin that creates these today. */
  origin: 'cockpit';
  cardId: string;
  effectId: string;
  createdAt?: unknown;
  createdBy: string;
  /**
   * When the follow-up left `open`, and who moved it (#374). Written by the
   * client — the only fields `firestore.rules` lets a member touch here — and
   * set back to `null` on an undo, so a reopened task carries no stale
   * signature. Absent on every task created before #374 shipped.
   */
  closedAt?: unknown;
  closedBy?: string | null;
}

/**
 * What a member says happened when they take a follow-up off the list (#374).
 * The two are NOT interchangeable: `done` means the visit/reading/relance took
 * place, `cancelled` means it will not — the flat changed hands, the tenant
 * paid before the relance went out. Collapsing them into one control would make
 * "fait" the only vocabulary available, and people would use it for both, which
 * turns the one signal we have into a lie.
 */
export type CockpitTaskOutcome = 'done' | 'cancelled';

export type CardTrustTier = 'low' | 'medium' | 'high';

export type CardStatus =
  | 'pending'
  | 'approved'
  | 'edited'
  | 'rejected'
  | 'snoozed'
  | 'executed'
  | 'failed'
  | 'handled_externally'
  | 'superseded'
  /**
   * Auto-scheduled card awaiting automatic execution after a delay. The
   * backend `processAutoCards` scheduled function promotes it to
   * `executed` once the timer elapses; the owner can cancel beforehand.
   */
  | 'scheduled_auto'
  /**
   * Transient processing lock held by the backend's `processAutoCards` while an
   * auto-card's effect runs. NON-terminal on purpose: a run that crashes
   * mid-flight leaves the card here — clearly incomplete — instead of in a
   * terminal success status, and the reaper re-picks stale `executing` cards.
   * Normally lasts under a second.
   *
   * It was missing from this mirror until the drift guard
   * (`cockpit-typeMirrorDrift.test.ts`, audit #83 point 67) was written, so the
   * one status that exists *because* something crashed was the one the UI could
   * not name.
   */
  | 'executing'
  /**
   * Multi-step workflow card parked on a WAIT_INBOUND step, waiting for
   * an external event (inbound email on the same thread, timer, or
   * manual nudge) before its current step can advance.
   */
  | 'waiting_external';

/**
 * Terminal card statuses — a card in one of these states is resolved/closed and
 * no longer part of the active working set. Single source of truth shared by
 * the cockpit store (bounds its live feed to `not-in` these) and the inbox
 * store (scopes its card listener the same way) so the two never drift apart.
 */
export const TERMINAL_CARD_STATUSES: ReadonlySet<CardStatus> = new Set<CardStatus>([
  'executed',
  'approved',
  'rejected',
  'handled_externally',
  'superseded',
  'failed'
]);

/** Same set as an array for Firestore `in` / `not-in` filters (≤10 values). */
export const TERMINAL_CARD_STATUS_ARRAY: CardStatus[] = [...TERMINAL_CARD_STATUSES];

/**
 * Mirrored from `backend/functions/src/cockpit/types.ts`. Two families:
 * typed roles map to a formal Proporio entity (tenant, owner, contractor,
 * syndic, agency); contextual roles are loose classifications useful for
 * filtering + AI matching but with no formal entity backing.
 *
 * `unclassified` is the default for newly auto-detected contacts pending
 * owner classification.
 */
export type ContactRole =
  | 'tenant'
  | 'owner'
  | 'contractor'
  | 'syndic'
  | 'agency'
  | 'co_owner'
  | 'neighbor'
  | 'insurance'
  | 'bank'
  | 'lawyer'
  | 'notary'
  | 'bailiff'
  | 'accountant'
  | 'expert'
  | 'administration'
  | 'trusted_person'
  | 'other'
  | 'unknown'
  | 'unclassified'
  | 'system';

export interface CardEntities {
  amount?: { value: number; currency: string };
  dueDate?: string;
  item?: string;
  people?: { name: string; role: ContactRole }[];
}

/**
 * Free-form display row for the split-view "what the AI extracted" panel.
 * `system` rows are locked (e.g. detected from active lease), `user` rows
 * are editable in the workflow stepper Step 1. When absent on a card, the
 * UI derives a default list from `CardEntities` via `toEntityRows()`.
 */
export interface EntityRow {
  label: string;
  value: string;
  note?: string;
  source: 'user' | 'system';
}

/**
 * Curated 200-char snippet of the first outgoing email a workflow will
 * send. Lets the read-only split-view show a tone preview without the
 * owner opening the full draft. Falls back to a truncated `draftBody`
 * when absent.
 */
export interface FirstMailPreview {
  stepIdx: number;
  to: string;
  subject: string;
  snippet: string;
  /**
   * True quand la carte repondra par WhatsApp (#371). Sans ce champ, l'apercu
   * s'annonce « aperçu du 1er mail » avec une enveloppe sur une carte dont la
   * reponse ne passera jamais par un mail.
   */
  isWhatsapp?: boolean;
}

export interface EvidenceSpan {
  field: string;
  start: number;
  end: number;
}

export interface CardAttachment {
  filename: string;
  mime: string;
  size: number;
  thumbUrl?: string;
  /** GCS path of the stored file — used to resolve a download URL (B9). */
  storagePath?: string;
  /**
   * Directly-usable, token-bearing download URL. Set for attachments that must
   * render/open without a `getDownloadURL` resolve — e.g. tenant repair photos,
   * which live in the tenant workspace and aren't resolvable from the owner
   * workspace by storage path.
   */
  url?: string;
}

export interface CardResolution {
  resolvedBy?: string;
  resolvedAt?: string;
  resultRef?: string;
  rejectionReason?: string;
  snoozeUntil?: string;
  externalReplyMessageId?: string;
  externalReplyPreview?: string;
  supersededByCardId?: string;
  /** When a `scheduled_auto` card should auto-execute (ISO). */
  autoExecuteAt?: string;
  /** Which effect triggered the auto window (for audit). */
  autoExecuteEffectId?: string;
  /** Set to true once the card was cancelled before the window closed. */
  autoCancelled?: boolean;
}

/**
 * A single concrete side-effect the AI proposes. A card can bundle several
 * effects (e.g. "send reply" + "log expense"); the owner can uncheck any
 * before executing. The primary button label is built from the enabled set.
 */
export interface ProposedEffect {
  id: string;
  type: CardActionType;
  /** Default true. Owner can uncheck to exclude from the bundle. */
  enabled: boolean;
  /** Per-effect trust; defaults to the card-level trust tier. */
  trustTier: CardTrustTier;
  /**
   * Human-readable short verb phrase — used in the primary button.
   *
   * Authored in ENGLISH by the backend (it doubles as the log/audit wording).
   * Never render it raw: go through `describeEffect` / `localizedActionLabel`,
   * which prefer {@link labelKey}. See issue #97.
   */
  label: string;
  /** Icon (Material Symbols name) for inline display. */
  icon: string;
  /** One-line preview of what will happen, shown under the label. English — see {@link label}. */
  preview: string;
  /**
   * i18n key for {@link label}, resolved in the owner's language. Set by the
   * backend on `NO_ACTION` effects, whose meaning lives entirely in the
   * authored label (there is no payload for `describeEffect` to ground a rich
   * title in, unlike ADD_EXPENSE / CREATE_REPAIR / …).
   *
   * Absent on cards written before the key existed — the raw `label` then
   * remains the fallback, which is why it is never dropped.
   */
  labelKey?: string;
  /** Interpolation params for {@link labelKey} (e.g. `{ year: '2026' }`). */
  labelParams?: Record<string, string>;
  /** i18n key for {@link preview}. See {@link labelKey}. */
  previewKey?: string;
  /** Interpolation params for {@link previewKey}. */
  previewParams?: Record<string, string>;
  /** Structured data passed to the backend when executed. */
  payload?: Record<string, unknown>;
  /** Optional inline-editable text body (e.g. draft reply). */
  draftBody?: string;
  /** After execution, the Firestore path or ID of the resulting artefact. */
  resultRef?: string;
  /**
   * Opt-in auto-execution window. When > 0, the card is created with
   * status = `scheduled_auto` and the backend scheduler promotes it to
   * `executed` after this delay. The cockpit UI shows a countdown +
   * Cancel button while the window is open.
   */
  autoExecuteAfterMs?: number;
  /* ---- Per-effect execution status (V1.1 — anti-double-execution) ---- */
  /**
   * Persisted execution outcome of THIS effect. `undefined` = never run.
   * `executed` effects are never re-dispatched; a retry of a `failed` card
   * only re-runs effects that aren't `executed`. Mirrors the backend
   * `ProposedEffect` in `backend/functions/src/cockpit/types.ts`.
   */
  status?: 'executed' | 'failed' | 'skipped';
  /** Structured failure code when `status === 'failed'` (see effect-requirements.ts codes). */
  errorCode?: string;
  /** Human-readable failure message, surfaced in the recovery UI. */
  errorMessage?: string;
  /** ISO timestamp stamped when `status` was last set. */
  executedAt?: string;
}

/**
 * Append-only audit event attached to a card. Every state change (AI
 * classification, user view/edit/approve, downstream side-effects,
 * undos, supersession) produces one entry. The UI renders these in the
 * History tab; the backend is the source of truth.
 */
export type ActionEventType =
  | 'RECEIVED'
  | 'CLASSIFIED'
  | 'PROPOSED'
  | 'VIEWED'
  | 'EDITED'
  | 'APPROVED'
  | 'REJECTED'
  | 'SNOOZED'
  | 'EXECUTED'
  | 'COMPLETED'
  | 'FAILED'
  | 'UNDONE'
  | 'SUPERSEDED_BY'
  | 'HANDLED_EXTERNALLY'
  | 'REOPENED'
  /** Multi-step workflow: step N completed, step N+1 now active. */
  | 'STEP_ADVANCED'
  /** Card parked in `waiting_external` (sent an outbound mail awaiting a reply, or owner-parked). */
  | 'WAITING'
  /** Multi-step workflow: a waiting_external step's wait window elapsed. */
  | 'WAIT_TIMEOUT'
  /** Multi-step workflow: a waiting_external step is approaching its deadline (early warning). */
  | 'WAIT_EXPIRING'
  /** Identity resolver wrote (or owner corrected) the card's contact/property/contract link. */
  | 'LINKAGE_CHANGED'
  /** EmailCaseLink (Phase 3): an email was attached to this card. */
  | 'LINK_ATTACHED'
  /** EmailCaseLink: an existing link was superseded (detached). */
  | 'LINK_DETACHED'
  /** EmailCaseLink: the user confirmed an auto-link. */
  | 'LINK_CONFIRMED'
  /** AI tool-call fired against the cockpit tools registry (Pillar 2 stage 2). */
  | 'TOOL_CALLED'
  /** AI tool-call failed (timeout, permission, not-found). */
  | 'TOOL_FAILED'
  /** A linked domain entity (repair, meter transfer…) changed status outside the cockpit. */
  | 'LINKED_STATUS'
  /** A workspace member was made responsible for the card, replaced, or cleared. */
  | 'ASSIGNED';

/**
 * Who is responsible for the card inside the workspace. Mirrors the backend
 * `CardAssignee` (`backend/functions/src/cockpit/types.ts`) — the two are
 * compared field by field by `__tests__/cockpit-assigneeDrift.test.ts`, so
 * renaming anything here breaks the backend suite on purpose.
 *
 * Written ONLY by the `assignCard` callable: the Firestore rules keep client
 * updates to `status`/`resolution`/`updatedAt`. Absent (never `null`) when
 * nobody has taken the card.
 */
/**
 * Backend-resolved facts about documents the cockpit client never reads, so the
 * pure pre-flight check can consult them without a Firestore round-trip. Mirrors
 * backend `CardReadiness` in `cockpit/executors/effectRequirements.ts`.
 *
 * A key is `false` only when a read confirmed the absence; a read error omits the
 * key rather than writing `false`, and every predicate treats absence as ready.
 */
export interface CardReadiness {
  /** ISO timestamp of the resolution, for debugging a stale-looking card. */
  checkedAt: string;
  /** The linked property has at least one meter (CREATE_METER_TRANSFER). */
  propertyHasMeters?: boolean;
  /** The linked contract records a deposit amount (UPDATE_CONTRACT release). */
  contractHasDeposit?: boolean;
  /** The linked contract has a start date (CONVERT_LEASE_TO_NINE_YEAR). */
  contractHasStartDate?: boolean;
  /** The linked contract has an end date (UPDATE_CONTRACT renew). */
  contractHasEndDate?: boolean;
}

/**
 * The AI-written narrative answering « où en est ce dossier ? ». Mirrors backend
 * `CardProgressSummary` in `cockpit/types.ts`.
 *
 * NOT `extraction.summary` / `summaryI18n`, which summarise the INBOUND MESSAGE
 * at ingestion. This one summarises the case file's PROGRESS, is written on
 * demand, and is cached on the card so a second reader pays nothing.
 *
 * The three `source*` fields are the staleness key — see
 * `isProgressSummaryStale` in `cockpit/services/card-progress.util.ts`.
 */
export interface CardProgressSummary {
  /** One paragraph, plain text — no markdown, must be rendered as text. */
  text: string;
  /** The language it was WRITTEN in, which may differ from the reader's UI. */
  lang: 'fr' | 'nl' | 'en' | 'de' | 'other';
  generatedAt: string;
  /** `events.length` at generation, INCLUDING the audit event of that write. */
  sourceEventCount: number;
  sourceStepIndex?: number;
  sourceStatus: CardStatus;
  model: string;
  generatedBy: string;
}

export interface CardAssignee {
  userId: string;
  displayName: string;
  assignedAt: string;
  assignedBy: string;
}

/**
 * A workspace member as the cockpit needs them: enough to render a picker and
 * a badge, nothing more. Projected from `Workspace.members` by the store so
 * presentation components never inject `WorkspaceStore` themselves.
 */
export interface CockpitMember {
  userId: string;
  displayName: string;
  email: string;
  role: string;
}

export interface ActionEvent {
  id: string;
  at: string;
  actor: {
    kind: 'system' | 'ai' | 'user';
    userId?: string;
    userName?: string;
    model?: string;
  };
  type: ActionEventType;
  /** Short human sentence — rendered directly in the timeline. */
  summary: string;
  /** Type-specific payload (model name, effect IDs touched, result refs…). */
  data?: Record<string, unknown>;
}

/* ------------------------------------------------------------------ */
/* Multi-step workflow primitives — mirror backend/functions/types.ts */
/* ------------------------------------------------------------------ */

export type WorkflowStepStatus =
  | 'pending'
  | 'active'
  | 'completed'
  | 'skipped'
  | 'waiting_external';

export type WorkflowStepKind =
  | 'context'
  | 'mail'
  | 'action'
  | 'wait'
  /** Service-provider picker (ranked cards, manual add). */
  | 'contractor'
  /** Decision recap + final rich mail (e.g. closing the loop with the tenant). */
  | 'recap'
  /** Decision point: owner picks one of `branchChoices`; reducer jumps to its nextStepId. */
  | 'choice';

export type WorkflowStepBadge = 'mail-ready' | 'auto-action';

/** A branch a `choice` step offers the owner. Mirror of backend. */
export interface WorkflowBranchChoice {
  id: string;
  label: string;
  nextStepId: string;
  reasoning?: string;
  /** Stable i18n key for `label`, stamped by the backend (label = fallback). */
  labelKey?: string;
  /** Stable i18n key for `reasoning`, stamped by the backend when present. */
  reasoningKey?: string;
}

/** Declarative auto-skip predicate evaluated by the reducer. Mirror of backend. */
export interface StepCondition {
  effectId: string;
  fieldPath: string;
  op: 'truthy' | 'equals';
  value?: unknown;
}

export interface WorkflowStepWait {
  kind: 'inbound_email' | 'timer' | 'manual';
  /** Match inbound messages on this thread to auto-advance. */
  threadId?: string;
  /** Optional sender filter. */
  fromEmail?: string;
  /** ISO — if set and elapsed, the backend emits WAIT_TIMEOUT. */
  untilIso?: string;
  /** Relative grace duration (ms) — backend stamps untilIso on step entry. */
  relativeMs?: number;
  hint: string;
}

export interface WorkflowStepUiHint {
  editableFields: string[];
  fieldLabels?: Record<string, string>;
  pickerKind?: 'service_provider' | 'contact' | 'property' | 'date_range' | 'none';
  pickerFilter?: Record<string, unknown>;
}

export interface WorkflowStep {
  id: string;
  index: number;
  title: string;
  /** Stable i18n key for `title`, stamped by the backend (title = fallback). */
  titleKey?: string;
  description: string;
  /** Copied into ActionCard.proposal.effects while this step is active. */
  effects: ProposedEffect[];
  status: WorkflowStepStatus;
  startedAt?: string;
  executedAt?: string;
  resultRefs?: string[];
  ui?: WorkflowStepUiHint;
  wait?: WorkflowStepWait;
  kind?: WorkflowStepKind;
  badge?: WorkflowStepBadge;
  target?: string;
  allowSkip?: boolean;
  reasoning?: string;
  /** Stable i18n key for `reasoning`, stamped by the backend when present. */
  reasoningKey?: string;
  /** Branching/skip metadata — mirror backend. Empty `[]` = terminal step. */
  allowedTransitions?: string[];
  branchChoices?: WorkflowBranchChoice[];
  condition?: StepCondition;
  visitCount?: number;
}

export interface CardWorkflow {
  id: string;
  /** Template identifier, e.g. "maintenance.leak@v1". */
  templateId: string;
  steps: WorkflowStep[];
  currentStepIndex: number;
  status: 'active' | 'completed' | 'aborted';
  createdAt: string;
  completedAt?: string;
}

export interface ActionCard {
  id: string;
  workspaceId: string;
  source: CardSource;
  sourceRef: {
    messageId?: string;
    mailDraftId?: string;
    eventId?: string;
  };
  threadId?: string;
  createdAt: string;
  senderName: string;
  senderEmail?: string;
  /**
   * L'adresse de la fiche rattachée, pour une carte arrivée par un canal qui
   * n'en porte pas (#438).
   *
   * `senderEmail` est l'adresse d'où le message est parti ; celle-ci est
   * l'autre canal de la même personne, retrouvé par son numéro. C'est elle qui
   * rend le choix Automatique / E-mail / WhatsApp possible sur une carte née
   * d'un WhatsApp — sans elle, l'écran n'y voit qu'un seul canal et n'offre
   * pas le choix. Le miroir exact de `ActionCard.linkedEmail` côté serveur.
   */
  linkedEmail?: string;
  /**
   * Le numéro de la fiche rattachée, pour une carte arrivée par un canal qui
   * n'en porte pas (#438) — le miroir exact de `linkedEmail`, dans l'autre sens.
   *
   * `senderPhone` est le numéro d'où le message est parti ; une carte née d'un
   * e-mail n'en a pas. Sans celui-ci, le choix Automatique / E-mail / WhatsApp
   * n'existait que sur les cartes WhatsApp : un locataire qui écrit un e-mail
   * ne pouvait jamais recevoir la réponse sur WhatsApp, même quand sa fiche
   * portait un numéro qu'il avait explicitement coché.
   *
   * Présent UNIQUEMENT si la personne a consenti — le serveur ne le remplit
   * pas autrement, et l'écran n'a donc pas à revérifier.
   */
  linkedPhone?: string;
  /**
   * WhatsApp origin (#367). Present exactly when `source` is
   * `INBOUND_WHATSAPP`, where there is no address to read. The requirement
   * predicates need it: without it a WhatsApp card looks like a card with no
   * recipient at all, and the Approve button stays blocked forever.
   */
  senderPhone?: string;
  senderWaId?: string;
  senderRole: ContactRole;
  subject: string;
  model?: string;
  tokenCost?: { input: number; output: number; usd: number };

  /** Detected/derived languages. `source` is the inbound email's language. */
  language?: {
    source?: string;
    ownerSummary?: string;
    outboundReply?: string;
  };

  classification: {
    intent: CardIntent;
    scenarioId?: string;
    propertyId?: string;
    propertyLabel?: string;
    contractId?: string;
    priority: CardPriority;
    confidence: number;
  };

  extraction: {
    summary: string;
    /**
     * Owner summary pre-translated into all 4 UI languages (en/fr/nl/de),
     * written once at ingestion so the cockpit can switch it instantly when
     * the user changes the app language. Falls back to `summary` when absent.
     */
    summaryI18n?: { en: string; fr: string; nl: string; de: string };
    entities: CardEntities;
    evidenceSpans?: EvidenceSpan[];
    displayEntities?: EntityRow[];
  };

  /**
   * Legacy single-action shape retained for backend + firestore-store
   * compatibility. New UI reads `effects` instead; `deriveEffects()` builds
   * an effects array from the legacy shape when `effects` is absent.
   */
  proposal: {
    actionType: CardActionType;
    trustTier: CardTrustTier;
    draftBody?: string;
    structuredPayload?: Record<string, unknown>;
    nextStep: string;
    /** One-sentence AI justification shown in the collapsed outcome panel. */
    rationale?: string;
    /** Bundle of concrete effects (new model). Falls back to deriveEffects. */
    effects?: ProposedEffect[];
    firstMailPreview?: FirstMailPreview;
  };

  sourceBody: string;
  attachments?: CardAttachment[];

  status: CardStatus;
  resolution?: CardResolution | null;
  /**
   * ISO timestamp set when the owner marked the card as seen/read (universal
   * "Vu" baseline, callable `markCardSeen`). The card stays `pending` but drops
   * out of the "needs you now" lane (unless high-stakes). Mirrors backend
   * `ActionCard.seenAt`. Additive — older cards have it unset.
   */
  seenAt?: string;
  /**
   * Workspace member made responsible for this card (`assignCard` callable).
   * Absent when nobody has taken it — unassigning deletes the field rather than
   * writing `null`. Mirrors backend `ActionCard.assignee`. Additive — older
   * cards have it unset.
   */
  assignee?: CardAssignee;
  /**
   * Facts about OTHER Firestore documents, resolved by the backend when the card
   * was created and refreshed when the owner re-links it. Mirrors backend
   * `ActionCard.readiness`. Additive — older cards have it unset, and the
   * pre-flight predicates are fail-open on `undefined`.
   *
   * The rule that keeps this bag from growing: do not persist what is DERIVABLE
   * from the card — persist only what is not knowable client-side.
   */
  readiness?: CardReadiness;
  /**
   * The cached AI narrative of the case file's PROGRESS, written on demand by
   * the `summarizeCardProgress` callable and never automatically. Absent until
   * someone asks for it. Server-written only — `firestore.rules` restricts the
   * client to `status`/`resolution`/`updatedAt`.
   */
  progressSummary?: CardProgressSummary;
  /** Append-only audit log. */
  events?: ActionEvent[];

  /**
   * Optional multi-step workflow. When present the card represents one
   * issue tracked from start to finish: `proposal.effects` always mirrors
   * `workflow.steps[workflow.currentStepIndex].effects`, so existing
   * rendering continues to work. Completing the active step advances the
   * cursor server-side.
   */
  workflow?: CardWorkflow;

  /**
   * Top-level wait condition for a card parked in `waiting_external`
   * WITHOUT a multi-step workflow — set either automatically when a simple
   * card sent an outbound mail that expects a reply (`kind: 'inbound_email'`,
   * carries `threadId`/`fromEmail`/`untilIso`) or manually by the owner via
   * "Mettre en attente" (`kind: 'manual'`). Workflow cards keep their wait on
   * `workflow.steps[].wait`; this field is the no-workflow equivalent so the
   * dashboard's "En attente" section and the inbound matcher can treat both
   * the same way. Cleared on reopen / resolve.
   */
  wait?: WorkflowStepWait;

  /**
   * Who/what/which this card is about — resolved identity + property +
   * contract with confidence scoring, written by the backend
   * `identityResolver`. The UI shows this as the "Linked to" panel.
   * See PERSONAS-AND-LINKAGE.md for confidence thresholds.
   */
  linkage?: CardLinkage;

  /**
   * Live status of the domain entities this card produced (repairs, meter
   * transfers…), kept in sync by the backend when the entity changes outside
   * the cockpit. Lets the card reflect what the owner did directly. Mirrors
   * backend `ActionCard.linkedEntities`.
   */
  linkedEntities?: {
    id: string;
    collection: string;
    status: string;
    label?: string;
    at: string;
  }[];
}

/* ------------------------------------------------------------------ */
/* Linkage — mirrors backend `types.ts`                               */
/* ------------------------------------------------------------------ */

export type LinkageSource =
  | 'contact_index'
  | 'tenant_contact_email'
  | 'service_provider_email'
  | 'thread_continuity'
  | 'ai_tool_resolved'
  | 'owner_manual'
  | 'internal_event';

export interface LinkageRef {
  id: string;
  confidence: number;
  source: LinkageSource;
  label?: string;
}

export interface CardLinkageCandidate {
  id: string;
  confidence: number;
  label?: string;
  reason?: string;
}

export interface CardLinkage {
  contact?: LinkageRef;
  property?: LinkageRef;
  contract?: LinkageRef;
  /** Tenants the owner picked, in order — the first is the addressee of
   * tenant mails. Absent = the tenants of the linked contract. */
  tenants?: LinkageRef[];
  candidates?: {
    contacts?: CardLinkageCandidate[];
    properties?: CardLinkageCandidate[];
    contracts?: CardLinkageCandidate[];
  };
  resolvedAt: string;
}

export interface CockpitFilters {
  sources: CardSource[];
  intents: CardIntent[];
  priorities: CardPriority[];
  statuses: CardStatus[];
  savedView:
    | 'default'
    | 'snoozed'
    | 'executed'
    | 'rejected'
    | 'failed'
    | 'handled_externally';
  searchText: string;
  /**
   * Optional deep-link narrow: when populated, the feed only shows
   * cards whose id is in this set (in addition to every other filter).
   * Driven by `?cards=id1,id2` on the cockpit route. Empty / null means
   * "no narrow", i.e. honour the other filters as-is.
   */
  cardIds?: string[] | null;
  /**
   * Responsible-member narrow. `null`/absent = everybody (no narrow),
   * {@link ASSIGNEE_FILTER_ME} = the signed-in user's cards,
   * {@link ASSIGNEE_FILTER_UNASSIGNED} = cards nobody has taken, any other
   * string = that member's uid. Optional so existing `CockpitFilters` literals
   * keep compiling.
   */
  assignee?: string | null;
}

/** Sentinel: "cards assigned to whoever is signed in" (resolved at filter time). */
export const ASSIGNEE_FILTER_ME = '__me__';
/** Sentinel: "cards nobody has taken". */
export const ASSIGNEE_FILTER_UNASSIGNED = '__unassigned__';

export const DEFAULT_FILTERS: CockpitFilters = {
  sources: [],
  intents: [],
  priorities: [],
  statuses: [],
  savedView: 'default',
  searchText: '',
  cardIds: null,
  assignee: null,
};

/**
 * Single implementation of the assignee narrow, called by BOTH cockpit stores
 * (the Firestore one and the in-memory demo one) so the demo can never diverge
 * from what the real feed shows.
 *
 * `ASSIGNEE_FILTER_ME` with no signed-in user matches nothing rather than
 * everything: showing the whole feed under a "my cards" label would be a lie.
 */
export function matchesAssigneeFilter(
  card: Pick<ActionCard, 'assignee'>,
  filter: string | null | undefined,
  currentUserId: string | null
): boolean {
  if (!filter) return true;
  if (filter === ASSIGNEE_FILTER_UNASSIGNED) return !card.assignee;
  if (filter === ASSIGNEE_FILTER_ME) {
    return !!currentUserId && card.assignee?.userId === currentUserId;
  }
  return card.assignee?.userId === filter;
}

/**
 * Returns the card's effect bundle, deriving it from the legacy single-action
 * proposal when the new `effects` field is absent. Keeps existing seed cards
 * and Firestore documents renderable without a data migration.
 */
export function getCardEffects(card: ActionCard): ProposedEffect[] {
  if (card.proposal.effects?.length) return card.proposal.effects;
  return [buildLegacyEffect(card)];
}

/**
 * Sender display name with degenerate values neutralised. Mirrors the backend
 * `sanitizeSenderName` guard so a literal "undefined undefined" / "null" /
 * blank — the residue of a `${firstName} ${lastName}` builder with missing
 * fields — never renders. Strips only whole "undefined"/"null" tokens, so real
 * names pass through untouched. Returns '' when nothing usable remains; callers
 * fall back to the email, or to a localized "Unknown sender" via
 * {@link CardPresenter.senderDisplayName}. Single source of truth for the rule
 * across the cockpit UI.
 */
export function cleanSenderName(card: Pick<ActionCard, 'senderName'>): string {
  return (card.senderName ?? '')
    .trim()
    .split(/\s+/)
    .filter((t) => t.length > 0 && !/^(undefined|null)$/i.test(t))
    .join(' ')
    .trim();
}

function buildLegacyEffect(card: ActionCard): ProposedEffect {
  const p = card.proposal;
  const entity = card.extraction.entities;
  const amt = entity.amount;
  const amountText = amt
    ? `${amt.value.toLocaleString('en-EU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${amt.currency}`
    : '';

  switch (p.actionType) {
    case 'DRAFT_REPLY':
      return {
        id: 'effect_reply',
        type: 'DRAFT_REPLY',
        enabled: true,
        trustTier: p.trustTier,
        label: `Send reply to ${cleanSenderName(card).split(' ')[0] || 'them'}`,
        icon: 'mail',
        preview: `An email will be sent to ${card.senderEmail ?? (cleanSenderName(card) || 'the sender')}.`,
        draftBody: p.draftBody,
        payload: { threadId: card.threadId }
      };
    case 'SEND_REMINDER':
      return {
        id: 'effect_reminder',
        type: 'SEND_REMINDER',
        enabled: true,
        trustTier: p.trustTier,
        label: 'Send reminder',
        icon: 'notifications_active',
        preview: `A ${((p.structuredPayload as { tier?: string })?.tier) ?? 'formal'} reminder will be sent${amountText ? ` regarding ${amountText}` : ''}.`,
        draftBody: p.draftBody,
        payload: p.structuredPayload
      };
    case 'ADD_EXPENSE':
      return {
        id: 'effect_expense',
        type: 'ADD_EXPENSE',
        enabled: true,
        trustTier: p.trustTier,
        label: amountText ? `Log ${amountText} expense` : 'Log expense',
        icon: 'payments',
        preview: `An expense will be recorded on ${card.classification.propertyLabel ?? 'the property'}.`,
        payload: p.structuredPayload
      };
    case 'CREATE_CASE':
      return {
        id: 'effect_case',
        type: 'CREATE_CASE',
        enabled: true,
        trustTier: p.trustTier,
        label: 'Open maintenance case',
        icon: 'build_circle',
        preview: `A new case will be opened on ${card.classification.propertyLabel ?? 'the property'}.`,
        payload: p.structuredPayload
      };
    case 'UPDATE_CONTRACT': {
      const sp = p.structuredPayload as { field?: string; newValue?: string | number } | undefined;
      return {
        id: 'effect_contract',
        type: 'UPDATE_CONTRACT',
        enabled: true,
        trustTier: p.trustTier,
        label: sp?.field ? `Update ${sp.field}` : 'Update contract',
        icon: 'description',
        preview: sp?.field && sp.newValue !== undefined
          ? `Contract field "${sp.field}" will change to ${sp.newValue}.`
          : 'The contract will be updated.',
        payload: p.structuredPayload
      };
    }
    case 'APPLY_INDEXATION': {
      const sp = p.structuredPayload as { newRent?: number; effectiveDate?: string } | undefined;
      return {
        id: 'effect_indexation',
        type: 'APPLY_INDEXATION',
        enabled: true,
        trustTier: p.trustTier,
        label: sp?.newRent ? `Apply new rent ${sp.newRent.toFixed(2)} EUR` : 'Apply indexation',
        icon: 'trending_up',
        preview: sp?.newRent
          ? `New monthly rent of ${sp.newRent.toFixed(2)} EUR will take effect${sp.effectiveDate ? ` on ${sp.effectiveDate}` : ''}.`
          : 'A rent indexation will be applied.',
        draftBody: p.draftBody,
        payload: p.structuredPayload
      };
    }
    case 'NO_ACTION':
    default:
      return {
        id: 'effect_noop',
        type: 'NO_ACTION',
        enabled: true,
        trustTier: 'low',
        label: 'Mark as handled',
        labelKey: 'cockpit.effectLabel.markHandled',
        icon: 'check_circle_outline',
        preview: 'No downstream action — the card will be archived.',
        previewKey: 'cockpit.effectPreview.markHandled',
        payload: p.structuredPayload
      };
  }
}
